import React, { useEffect, useRef, useState, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const MAX_DEPTH = 8;  // 최대 재귀 깊이
const MIN_DEPTH = 0;  // 최소 재귀 깊이
const ANIMATION_SPEED = 500; // 깊이 변화 애니메이션 속도 (ms)

const SierpinskiTriangle = () => {
  const canvasRef = useRef(null);
  const [depth, setDepth] = useState(5); // 현재 재귀 깊이
  const [isAnimating, setIsAnimating] = useState(false);
  const [colorMode, setColorMode] = useState('rainbow'); // 'rainbow', 'monochrome', 'gradient'
  const animationRef = useRef(null);
  const directionRef = useRef(1); // 1: 증가, -1: 감소

  // 삼각형을 그리는 재귀 함수
  const drawSierpinskiTriangle = useCallback((ctx, x1, y1, x2, y2, x3, y3, depth, currentDepth = 0) => {
    // 재귀 종료 조건
    if (currentDepth >= depth) {
      // 색상 설정
      let fillStyle;
      if (colorMode === 'rainbow') {
        // 무지개 색상 (깊이에 따라 다른 색상)
        const hue = (currentDepth * 30) % 360;
        fillStyle = `hsl(${hue}, 80%, 60%)`;
      } else if (colorMode === 'monochrome') {
        // 단색 (깊이에 따라 명도만 변화)
        const lightness = 70 - currentDepth * 7;
        fillStyle = `hsl(210, 80%, ${lightness}%)`;
      } else if (colorMode === 'gradient') {
        // 그라데이션 (위치에 따라 색상 변화)
        const normalizedY = (y1 + y2 + y3) / 3 / CANVAS_HEIGHT;
        const hue = 180 + normalizedY * 180;
        fillStyle = `hsl(${hue}, 80%, 60%)`;
      }
      
      // 삼각형 채우기
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.closePath();
      ctx.fillStyle = fillStyle;
      ctx.fill();
      
      // 테두리 그리기 (더 깊은 단계에서는 더 얇은 선)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = Math.max(0.1, 1 - currentDepth * 0.1);
      ctx.stroke();
      
      return;
    }
    
    // 각 변의 중점 계산
    const midX1 = (x1 + x2) / 2;
    const midY1 = (y1 + y2) / 2;
    const midX2 = (x2 + x3) / 2;
    const midY2 = (y2 + y3) / 2;
    const midX3 = (x3 + x1) / 2;
    const midY3 = (y3 + y1) / 2;
    
    // 세 개의 작은 삼각형으로 재귀 호출
    drawSierpinskiTriangle(ctx, x1, y1, midX1, midY1, midX3, midY3, depth, currentDepth + 1);
    drawSierpinskiTriangle(ctx, midX1, midY1, x2, y2, midX2, midY2, depth, currentDepth + 1);
    drawSierpinskiTriangle(ctx, midX3, midY3, midX2, midY2, x3, y3, depth, currentDepth + 1);
  }, [colorMode]);

  // 전체 캔버스 그리기
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 배경 그라데이션
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    gradient.addColorStop(0, '#1a1a2e');
    gradient.addColorStop(1, '#16213e');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 초기 삼각형 좌표 (정삼각형)
    const margin = 20;
    const centerX = CANVAS_WIDTH / 2;
    const topY = margin;
    const bottomY = CANVAS_HEIGHT - margin;
    const width = CANVAS_HEIGHT - 2 * margin;
    const height = Math.sqrt(3) / 2 * width;
    
    const x1 = centerX;               // 꼭대기
    const y1 = topY;
    const x2 = centerX - width / 2;   // 왼쪽 아래
    const y2 = bottomY;
    const x3 = centerX + width / 2;   // 오른쪽 아래
    const y3 = bottomY;
    
    // 시에르핀스키 삼각형 그리기
    drawSierpinskiTriangle(ctx, x1, y1, x2, y2, x3, y3, depth);
    
    // 현재 깊이 표시
    ctx.font = 'bold 16px Arial';
    ctx.fillStyle = 'white';
    ctx.textAlign = 'right';
    ctx.fillText(`깊이: ${depth}`, CANVAS_WIDTH - 20, 30);
  }, [depth, drawSierpinskiTriangle]);

  // 자동 애니메이션 (깊이 증가/감소)
  const animateDepth = useCallback(() => {
    setIsAnimating(true);
    
    const animate = () => {
      setDepth(prevDepth => {
        // 방향 전환 체크
        if (prevDepth >= MAX_DEPTH) {
          directionRef.current = -1;
        } else if (prevDepth <= MIN_DEPTH) {
          directionRef.current = 1;
        }
        
        // 새 깊이 계산
        return prevDepth + directionRef.current;
      });
      
      // 다음 단계 애니메이션
      animationRef.current = setTimeout(animate, ANIMATION_SPEED);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, []);

  // 애니메이션 중지
  const stopAnimation = () => {
    if (animationRef.current) {
      clearTimeout(animationRef.current);
    }
    setIsAnimating(false);
  };

  // 깊이 변경 핸들러
  const handleDepthChange = (e) => {
    stopAnimation();
    setDepth(parseInt(e.target.value));
  };

  // 색상 모드 변경 핸들러
  const handleColorModeChange = (mode) => {
    setColorMode(mode);
  };

  // 초기화 및 정리
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    draw();
    
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [draw]);

  // 애니메이션 토글
  const toggleAnimation = () => {
    if (isAnimating) {
      stopAnimation();
    } else {
      animateDepth();
    }
  };

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Sierpinski Triangle</h2>
      <div style={canvasContainerStyle}>
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={canvasStyle} />
      </div>
      
      <div style={controlsStyle}>
        <div style={controlGroupStyle}>
          <label htmlFor="depth" style={labelStyle}>깊이: {depth}</label>
          <input
            id="depth"
            type="range"
            min={MIN_DEPTH}
            max={MAX_DEPTH}
            value={depth}
            onChange={handleDepthChange}
            style={sliderStyle}
          />
        </div>
        
        <div style={controlGroupStyle}>
          <button onClick={toggleAnimation} style={isAnimating ? pauseButtonStyle : playButtonStyle}>
            {isAnimating ? '정지' : '자동 변화'}
          </button>
        </div>
        
        <div style={controlGroupStyle}>
          <label style={labelStyle}>색상 모드:</label>
          <div style={colorButtonsStyle}>
            <button 
              onClick={() => handleColorModeChange('rainbow')} 
              style={{
                ...colorButtonStyle, 
                background: colorMode === 'rainbow' 
                  ? 'linear-gradient(to right, violet, indigo, blue, green, yellow, orange, red)'
                  : '#aaa',
                color: colorMode === 'rainbow' ? 'white' : '#333'
              }}
            >
              무지개
            </button>
            <button 
              onClick={() => handleColorModeChange('monochrome')} 
              style={{
                ...colorButtonStyle, 
                background: colorMode === 'monochrome' 
                  ? 'linear-gradient(to bottom, #80a0ff, #2050a0)'
                  : '#aaa',
                color: colorMode === 'monochrome' ? 'white' : '#333'
              }}
            >
              단색
            </button>
            <button 
              onClick={() => handleColorModeChange('gradient')} 
              style={{
                ...colorButtonStyle, 
                background: colorMode === 'gradient' 
                  ? 'linear-gradient(to bottom, #50c0ff, #8030a0)'
                  : '#aaa',
                color: colorMode === 'gradient' ? 'white' : '#333'
              }}
            >
              그라데이션
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Styled Components ---
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  padding: '0.5rem',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const controlsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
  marginTop: '1.5rem',
  width: '80%',
  maxWidth: '500px'
};

const controlGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#e0e0e0'
};

const sliderStyle = {
  width: '100%',
  height: '8px',
  appearance: 'none',
  background: 'linear-gradient(to right, #2c5364, #4e94c4, #7fcdf3)',
  outline: 'none',
  borderRadius: '10px',
  cursor: 'pointer',
  '::-webkit-slider-thumb': {
    appearance: 'none',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'white',
    cursor: 'pointer'
  }
};

const buttonBaseStyle = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.2)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  outline: 'none'
};

const playButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #4f9cf9 0%, #2872b9 100%)',
  color: 'white'
};

const pauseButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #f56a6a 0%, #d93434 100%)',
  color: 'white'
};

const colorButtonsStyle = {
  display: 'flex',
  gap: '0.8rem',
  flexWrap: 'wrap'
};

const colorButtonStyle = {
  padding: '0.5rem 1rem',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  outline: 'none'
};

export default SierpinskiTriangle; 